"use client";

import { useState, useRef, useEffect, useCallback } from "react";

interface PlaceResult {
  label: string;
  name: string;
  state: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number;
  lng: number;
}

export interface ExtraOptions {
  year?: number;
  returnLatitude?: number;
  returnLongitude?: number;
  returnLocationLabel?: string;
}

interface Props {
  needsYear: boolean;
  yearLabel: string;
  defaultYear: number;
  needsReturnLocation: boolean;
  onSubmit: (opts: ExtraOptions) => void;
  isLoading: boolean;
}

export default function ExtraOptionsForm({
  needsYear,
  yearLabel,
  defaultYear,
  needsReturnLocation,
  onSubmit,
  isLoading,
}: Props) {
  const [year, setYear] = useState(defaultYear);

  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    setSearching(true);
    try {
      const res = await fetch(`/api/places?q=${encodeURIComponent(q)}`);
      const data: PlaceResult[] = res.ok ? await res.json() : [];
      setSuggestions(data);
      setShowDropdown(data.length > 0);
    } catch {
      setSuggestions([]); setShowDropdown(false);
    } finally {
      setSearching(false);
    }
  }, []);

  function handlePlaceInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setPlaceQuery(val);
    setSelectedPlace(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  }

  function handleSelectPlace(place: PlaceResult) {
    setSelectedPlace(place);
    setPlaceQuery(place.label);
    setSuggestions([]);
    setShowDropdown(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const opts: ExtraOptions = {};
    if (needsYear) opts.year = year;
    if (needsReturnLocation && selectedPlace) {
      opts.returnLatitude = selectedPlace.lat;
      opts.returnLongitude = selectedPlace.lng;
      opts.returnLocationLabel = selectedPlace.label;
    }
    onSubmit(opts);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-lg">
      {needsYear && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {yearLabel}
          </label>
          <input
            type="number"
            value={year}
            min={1900}
            max={2100}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      )}

      {needsReturnLocation && (
        <div ref={dropdownRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location at time of return
          </label>
          <p className="text-xs text-gray-400 mb-1">Leave blank to use birth location</p>
          <div className="relative">
            <input
              type="text"
              value={placeQuery}
              onChange={handlePlaceInput}
              onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
              placeholder="Search for a city…"
              autoComplete="off"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 pr-8"
            />
            {searching && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">...</span>
            )}
            {selectedPlace && !searching && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500 text-sm">✓</span>
            )}
          </div>
          {showDropdown && suggestions.length > 0 && (
            <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
              {suggestions.map((place, i) => (
                <li
                  key={i}
                  onMouseDown={() => handleSelectPlace(place)}
                  className="px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  {place.label}
                </li>
              ))}
            </ul>
          )}
          {selectedPlace && (
            <p className="mt-1 text-xs text-gray-400">
              {selectedPlace.lat.toFixed(4)}, {selectedPlace.lng.toFixed(4)}
            </p>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="bg-indigo-700 hover:bg-indigo-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
      >
        {isLoading ? "Generating…" : "Generate Report"}
      </button>
    </form>
  );
}
